import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Typography,
  CircularProgress,
  Box,
  useMediaQuery,
  useTheme,
  Grid,
  Alert,
  Tooltip,
  Button,
  Snackbar,
  Stack,
  Paper,
} from '@mui/material';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Table from '../../../common/components/Table/Table';
import {
  useE09DispatchData,
  useAddE09Sample,
  useUpdateE09Sample,
  useE09Finalize,
  useE09Validate,
  SortConfig,
  FilterConfig,
} from './hooks';
import { useDeleteE09Sample } from './hooks/useE09SampleMutation';
import { useE09FileDetails } from './hooks/useE09FileDetails';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import { useNavItems, usePermission } from '../../../hooks';
import { MainContent } from './e09Message.styled';
import { ContentPaper } from './ViewSamples.styled';
import { ContextMenuAction, SortDirection } from '../../../common/components/Table/types';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import VerifiedIcon from '@mui/icons-material/Verified';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { ValidationError } from '../../../hooks/useFileUpload';
import { useUser } from '../../../hooks/useUser';
import { AIIModal, E09EditModal } from './components';

// Custom hook for handling paginated errors
function usePaginatedErrors(errors: ValidationError[]) {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'ASC' | 'DESC' } | null>(
    null
  );

  // Ensure errors are properly formatted and assign IDs
  const processedErrors = useMemo(() => {
    if (!Array.isArray(errors)) {
      console.error('Errors is not an array:', errors);
      return [];
    }

    const processed = errors.map((error, index) => {
      // Ensure error has all required fields or provide defaults
      if (!error || typeof error !== 'object') {
        console.warn(`Invalid error at index ${index}:`, error);
        return {
          id: `error-${index}`,
          sampleId: null,
          version: null,
          field: 'unknown',
          errorCode: 'UnknownError',
          message: 'An unknown error occurred',
        };
      }

      return {
        id: `error-${index}`, // Add id for table row key
        sampleId: error.sampleId ?? null,
        version: error.version ?? null,
        field: error.field || 'unknown',
        errorCode: error.errorCode || 'Error',
        message: error.message || 'No error message provided',
      };
    });

    // Apply sorting if configured
    if (sortConfig) {
      return [...processed].sort((a, b) => {
        const aValue = a[sortConfig.field as keyof typeof a];
        const bValue = b[sortConfig.field as keyof typeof b];

        if (aValue === null) return sortConfig.direction === 'ASC' ? -1 : 1;
        if (bValue === null) return sortConfig.direction === 'ASC' ? 1 : -1;

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'ASC'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        // Default comparison for other types
        return sortConfig.direction === 'ASC'
          ? aValue < bValue
            ? -1
            : 1
          : aValue > bValue
            ? -1
            : 1;
      });
    }

    return processed;
  }, [errors, sortConfig]);

  // Calculate total error count
  const totalErrorCount = processedErrors.length;

  // Get the paginated slice of errors for the current page
  const paginatedErrors = useMemo(() => {
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;
    return processedErrors.slice(startIndex, endIndex);
  }, [processedErrors, currentPage, pageSize]);

  // Sort errors based on field and direction
  const sortErrors = useCallback((field: string, direction: 'ASC' | 'DESC') => {
    setSortConfig({ field, direction });
    setCurrentPage(0); // Reset to first page when sorting
  }, []);

  return {
    paginatedErrors,
    totalErrorCount,
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    sortErrors,
  };
}

// Custom Error Table Component that handles its own pagination
const PaginatedErrorTable: React.FC<{ errors: ValidationError[]; fileName?: string }> = ({
  errors,
  fileName,
}) => {
  const {
    paginatedErrors,
    totalErrorCount,
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    sortErrors,
  } = usePaginatedErrors(errors);

  // Define table columns with fixed widths to ensure alignment
  const columns = useMemo(
    () => [
      {
        Header: 'Despatch ID',
        accessor: 'sampleId',
        width: '100px', // Fixed width to ensure alignment
        Cell: ({ value }: { value: string | null }) => (value !== null ? value : '-'),
      },
      {
        Header: 'Version',
        accessor: 'version',
        width: '100px', // Fixed width to ensure alignment
        Cell: ({ value }: { value: string | null }) => (value !== null ? value : '-'),
      },
      {
        Header: 'Field',
        accessor: 'field',
        width: '200px', // Fixed width to ensure alignment
      },
      {
        Header: 'Error Code',
        accessor: 'errorCode',
        width: '200px', // Fixed width to ensure alignment
      },
      {
        Header: 'Message',
        accessor: 'message',
        width: '500px', // Fixed width to ensure alignment
        Cell: ({ value }: { value: string }) => (
          <Typography
            variant='body2'
            sx={{
              whiteSpace: 'pre-wrap', // Allow text to wrap to new lines
              wordBreak: 'break-word', // Break words to prevent overflow
              overflowWrap: 'break-word', // Ensure long words break and wrap
              lineHeight: 1.5, // Increase line height for better readability
              padding: '8px 0', // Add some padding for better spacing
            }}
          >
            {value}
          </Typography>
        ),
      },
    ],
    []
  );

  // Handle sorting
  const handleSort = (column: string, direction: 'ASC' | 'DESC' | undefined, searchColumn?: string, searchTerm?: string) => {
    if (direction) {
      sortErrors(column, direction);
    }
  };

  // Early return if there are no valid errors
  if (totalErrorCount === 0) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        mt: 2,
        overflow: 'hidden', // Prevents horizontal overflow
        '& .MuiTableContainer-root': {
          overflow: 'auto',
        },
        '& .MuiTable-root': {
          tableLayout: 'fixed', // Forces the table to respect column widths
        },
      }}
    >
      <Table
        data={paginatedErrors}
        columns={columns}
        moduleName={`Validation Errors for ${fileName || 'file'} (${totalErrorCount})`}
        pageSize={pageSize}
        page={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        totalCount={totalErrorCount}
        onSort={handleSort}
        viewMode={true} // Set to true to hide action buttons
        isRowClickable={false}
        disableAutoSizing={true} // Disable auto-sizing to use our fixed widths
      />
    </Paper>
  );
};

const ViewSamples: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fileId } = useParams<{ fileId: string }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navItems = useNavItems();

  // Get the user data directly to check permissions
  const { data: userData, isLoading: userLoading } = useUser();

  // Use a more flexible approach to check permissions
  const hasViewAccess = useMemo(() => {
    if (!userData || userLoading) return false;

    // Check if user has any e09-related permissions
    const hasAnyE09Permission = userData.modules?.some((module) => {
      // Check if this is the e09 module or contains e09 permissions
      if (module.url?.startsWith('/e09') && module.enabled && !module.isAdminDisabled) {
        return true;
      }

      // Check permissions within modules
      return module.permissions?.some(
        (permission) =>
          permission.url?.startsWith('/e09') && permission.enabled && !permission.isAdminDisabled
      );
    });

    return !!hasAnyE09Permission;
  }, [userData, userLoading]);

  // Check specifically for edit permission using the correct URL path
  const hasEditAccess = useMemo(() => {
    if (!userData || userLoading) return false;

    // Check for the "Edit Despatches" permission specifically
    return userData.modules?.some((module) =>
      module.permissions?.some(
        (permission) =>
          (permission.url === '/e09/despatches/edit' || permission.name === 'Edit Despatches') &&
          permission.enabled &&
          !permission.isAdminDisabled
      )
    );
  }, [userData, userLoading]);

  // Update permission variables for clarity
  const permissionLoading = userLoading;
  const hasPermission = hasViewAccess;
  const hasEditPermission = hasEditAccess;

  // State to track the row being edited
  const [editingRowId, setEditingRowId] = useState<string | number | null>(null);

  // State for EditModal
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);

  // State to track if a new row is being added
  const [isAddingRow, setIsAddingRow] = useState<boolean>(false);

  // Store a temporary new row
  const [newRow, setNewRow] = useState<any>(null);

  // State for validation errors
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  // State for notification messages
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    type: 'info',
  });

  // Add state for button loading
  const [validateCorrectLoading, setValidateCorrectLoading] = useState<boolean>(false);
  const [validateFinalizeLoading, setValidateFinalizeLoading] = useState<boolean>(false);

  // Add state for AII Modal
  const [isAIIModalOpen, setIsAIIModalOpen] = useState<boolean>(false);
  const [selectedAIIRow, setSelectedAIIRow] = useState<any | null>(null);

  // Add search state management
  const [searchColumn, setSearchColumn] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);

  // Add status filter state management
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // State for multiple sorts and filters
  const [sorts, setSorts] = useState<SortConfig[]>([]);
  const [filters, setFilters] = useState<FilterConfig[]>([]);

  // Mutation hooks for CRUD operations
  const addSampleMutation = useAddE09Sample();
  const updateSampleMutation = useUpdateE09Sample();
  const finalizeFile = useE09Finalize();
  const validateFile = useE09Validate();
  const deleteSampleMutation = useDeleteE09Sample();

  // Function to extract validation errors from API responses
  const extractValidationErrors = (errorResponse: any): ValidationError[] => {
    if (!errorResponse) return [];

    // Check common error response patterns
    if (errorResponse.errors && Array.isArray(errorResponse.errors)) {
      return errorResponse.errors;
    } else if (
      errorResponse.errorResponse &&
      errorResponse.errorResponse.errors &&
      Array.isArray(errorResponse.errorResponse.errors)
    ) {
      return errorResponse.errorResponse.errors;
    } else if (Array.isArray(errorResponse)) {
      return errorResponse;
    }

    return [];
  };

  // Get pagination parameters from URL
  const page = parseInt(searchParams.get('page') || '0', 10);
  const pageSize = parseInt(searchParams.get('size') || '10', 10);
  const sortField = searchParams.get('sort') || '';
  const sortDirection = (searchParams.get('direction') || '') as 'ASC' | 'DESC';

  // Check if edit mode is enabled via query parameter
  const isEditMode = searchParams.get('editMode') === 'true' && hasEditPermission;
  // Add explicit view mode flag
  const isViewMode = !isEditMode && hasPermission;

  const numericFileId = fileId ? parseInt(fileId, 10) : 0;
  const isValidFileId = !isNaN(numericFileId) && numericFileId > 0;

  // Helper function to parse multiple sorts from URL
  const parseSortsFromURL = useCallback((params: URLSearchParams): SortConfig[] => {
    const parsedSorts: SortConfig[] = [];
    params.getAll('sort').forEach(sortParam => {
      const [column, direction] = sortParam.split(',');
      if (column && direction) {
        parsedSorts.push({ column, direction: direction as 'ASC' | 'DESC' });
      }
    });
    return parsedSorts;
  }, []);

  // Helper function to update URL with multiple sorts
  const updateSortsInURL = useCallback((newSorts: SortConfig[], params: URLSearchParams) => {
    params.delete('sort'); // Remove all existing sort parameters
    newSorts.forEach((sort) => {
      params.append('sort', `${sort.column},${sort.direction}`);
    });
  }, []);

  // Helper function to parse multiple filters from URL
  const parseFiltersFromURL = useCallback((params: URLSearchParams): FilterConfig[] => {
    const parsedFilters: FilterConfig[] = [];
    // Iterate over all search params to find filter keys
    for (const [key, value] of params.entries()) {
      // We need to be careful not to pick up other params like 'page', 'size', 'sort', 'editMode', 'status'
      if (!['page', 'size', 'sort', 'direction', 'editMode', 'status'].includes(key)) {
        parsedFilters.push({ column: key, term: value });
      }
    }
    return parsedFilters;
  }, []);

  // Helper function to update URL with multiple filters
  const updateFiltersInURL = useCallback((newFilters: FilterConfig[], params: URLSearchParams) => {
    // Clear existing filter parameters (excluding known non-filter params)
    const keysToDelete = Array.from(params.keys()).filter(key =>
      !['page', 'size', 'sort', 'direction', 'editMode', 'status'].includes(key)
    );
    keysToDelete.forEach(key => params.delete(key));

    // Add new filter parameters
    newFilters.forEach((filter) => {
      if (filter.column && filter.term) {
        params.append(filter.column, filter.term);
      }
    });
  }, []);

  // Extract search parameters from URL on component mount
  useEffect(() => {
    const searchColParam = searchParams.get('searchColumn');
    const searchTermParam = searchParams.get('searchTerm');
    const statusParam = searchParams.get('status');

    if (searchColParam) setSearchColumn(searchColParam);
    if (searchTermParam) setSearchTerm(searchTermParam);
    if (statusParam) setStatusFilter(statusParam);

    // Parse multiple sorts and filters from URL
    const parsedSorts = parseSortsFromURL(searchParams);
    const parsedFilters = parseFiltersFromURL(searchParams);

    if (JSON.stringify(parsedSorts) !== JSON.stringify(sorts)) {
      setSorts(parsedSorts);
    }
    if (JSON.stringify(parsedFilters) !== JSON.stringify(filters)) {
      setFilters(parsedFilters);
    }
  }, [searchParams, parseSortsFromURL, parseFiltersFromURL, sorts, filters]);

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/e09/despatches');
  };

  // Reset validation errors
  const resetValidationErrors = () => {
    setValidationErrors([]);
  };

  // Update URL when pagination parameters change
  const updateSearchParams = (params: Record<string, string>) => {
    const newSearchParams = new URLSearchParams(searchParams);

    // Update the search params with the new values
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        newSearchParams.set(key, value);
      } else {
        newSearchParams.delete(key);
      }
    });

    // Preserve edit mode parameter if it exists
    if (isEditMode) {
      newSearchParams.set('editMode', 'true');
    }

    // Update the URL without reloading the page
    setSearchParams(newSearchParams);
  };

  // Handle status filter change
  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    updateSearchParams({ status });
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    updateSearchParams({ page: newPage.toString() });
  };

  // Handle page size change
  const handlePageSizeChange = (newSize: number) => {
    // First clear the size parameter to force a query parameter change
    const tempParams = new URLSearchParams(searchParams);
    tempParams.delete('size');
    tempParams.delete('page');
    setSearchParams(tempParams);

    // Then set it again with a slight delay to ensure URL changes are processed
    setTimeout(() => {
      updateSearchParams({
        page: '0', // Reset to first page when changing page size
        size: newSize.toString(),
      });
    }, 0);
  };

  // Handle sorting and searching
  const handleSort = useCallback((column: string, direction: SortDirection | undefined, searchColumn?: string, searchTerm?: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    
    // Handle multiple sorts
    let newSorts: SortConfig[] = [...sorts];
    if (direction) {
      const existingSortIndex = newSorts.findIndex(s => s.column === column);
      if (existingSortIndex >= 0) {
        if (newSorts[existingSortIndex].direction === direction) {
          newSorts.splice(existingSortIndex, 1); // Remove if same direction
        } else {
          newSorts[existingSortIndex].direction = direction; // Update direction
        }
      } else {
        newSorts.push({ column, direction }); // Add new sort
      }
    } else {
      newSorts = newSorts.filter(s => s.column !== column); // Remove sort
    }
    setSorts(newSorts);
    updateSortsInURL(newSorts, newSearchParams);

    // Handle multiple filters
    let newFilters: FilterConfig[] = [...filters];
    if (searchColumn && searchTerm && searchTerm.trim()) {
      const existingFilterIndex = newFilters.findIndex((f) => f.column === searchColumn);
      if (existingFilterIndex >= 0) {
        newFilters[existingFilterIndex].term = searchTerm.trim();
      } else {
        newFilters.push({ column: searchColumn, term: searchTerm.trim() });
      }
    } else if (searchColumn && (!searchTerm || !searchTerm.trim())) {
      newFilters = newFilters.filter((f) => f.column !== searchColumn);
    }
    setFilters(newFilters);
    updateFiltersInURL(newFilters, newSearchParams);
    
    // Preserve edit mode parameter if it exists
    if (isEditMode) {
      newSearchParams.set('editMode', 'true');
    }
    
    setSearchParams(newSearchParams);
  }, [sorts, filters, searchParams, setSearchParams, isEditMode, updateSortsInURL, updateFiltersInURL]);

  // Fetch dispatch data for the selected file with pagination parameters
  const {
    data: dispatchResponse,
    isLoading,
    isError,
    error,
  } = useE09DispatchData({
    e09FileId: numericFileId,
    page,
    size: pageSize,
    sorts: sorts,
    filters: filters,
    enabled: isValidFileId,
    searchColumn: searchColumn,
    searchTerm: searchTerm,
    status: statusFilter,
  });

  // Prepare data for the table
  const dispatchData = useMemo(() => {
    return dispatchResponse?.content || [];
  }, [dispatchResponse]);

  // Use the new hook to fetch file details
  const {
    data: fileDetails,
    isLoading: isFileDetailsLoading,
    isError: isFileDetailsError,
  } = useE09FileDetails({
    fileId: numericFileId,
    enabled: isValidFileId,
  });

  // Check if file status allows validation actions
  const isValidateCorrectEnabled = useMemo(() => {
    // Enable if file status is DRAFT or FINAL
    return fileDetails?.fileStatus === 'D' || fileDetails?.fileStatus === 'DRAFT';
  }, [fileDetails?.fileStatus]);

  const isValidateFinalizeEnabled = useMemo(() => {
    // Enable if file status does NOT have F or FINAL
    return fileDetails?.fileStatus !== 'F' && fileDetails?.fileStatus !== 'FINAL';
  }, [fileDetails?.fileStatus]);

  // Define columns for the table
  const columns = useMemo(
    () => [
      {
        Header: 'despatch_status',
        accessor: 'despatchStatus',
        width: '120px',
      },
      {
        Header: 'AII',
        accessor: 'aii',
        width: '120px',
        Cell: ({ value }: { value: any }) => {
          if (value === true) return 'Y';
          if (value === false) return '';
          return value !== null && value !== undefined ? String(value) : '';
        },
      },
      {
        Header: 'ibrs_sn',
        accessor: 'ibrsSn',
        width: '120px',
      },
      {
        Header: 'version',
        accessor: 'version',
        width: '100px',
      },
      {
        Header: 'change_by',
        accessor: 'changeBy',
        width: '120px',
      },
      {
        Header: 'comment',
        accessor: 'comment',
        width: '200px',
        Cell: ({ value }: { value: any }) => {
          if (value === true) return 'Y';
          if (value === false) return '';
          
          const commentText = value !== null && value !== undefined ? String(value) : '';
          
          if (!commentText) return '';
          
          // Truncate long comments (show first 50 characters)
          const maxLength = 50;
          const isLongComment = commentText.length > maxLength;
          const truncatedText = isLongComment 
            ? commentText.substring(0, maxLength) + '...' 
            : commentText;
          
          return (
            <Tooltip title={isLongComment ? commentText : ''} arrow placement="top">
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '300px',
                  cursor: isLongComment ? 'help' : 'default',
                }}
              >
                {truncatedText}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        Header: 'del_flag',
        accessor: 'delFlag',
        width: '100px',
      },
      {
        Header: 'valid_flag',
        accessor: 'validFlag',
        width: '100px',
      },
      {
        Header: 'mail_category',
        accessor: 'mailCategory',
        width: '120px',
      },
      {
        Header: 'format',
        accessor: 'format',
        width: '120px',
      },
      {
        Header: 'sending_ppo',
        accessor: 'sendingPpo',
        width: '120px',
      },
      {
        Header: 'oe_from_code',
        accessor: 'oeFromCode',
        width: '120px',
      },
      {
        Header: 'receiving_ppo',
        accessor: 'receivingPpo',
        width: '120px',
      },
      {
        Header: 'oe_to_code',
        accessor: 'oeToCode',
        width: '120px',
      },
      {
        Header: 'despatch_mail_subclass',
        accessor: 'despatchMailSubclass',
        width: '150px',
      },
      {
        Header: 'despatch_date',
        accessor: 'despatchDate',
        width: '150px',
      },
      {
        Header: 'despatch_number',
        accessor: 'despatchNumber',
        width: '150px',
      },
      {
        Header: 'despatch_id',
        accessor: 'despatchId',
        width: '150px',
      },
      {
        Header: 'exempt_flag',
        accessor: 'exemptFlag',
        width: '120px',
      },
      {
        Header: 'normal_net_weight',
        accessor: 'normalNetWeight',
        width: '150px',
      },
      {
        Header: 'normal_items',
        accessor: 'normalItems',
        width: '120px',
      },
      {
        Header: 'bulky_net_weight',
        accessor: 'bulkyNetWeight',
        width: '150px',
      },
      {
        Header: 'bulky_items',
        accessor: 'bulkyItems',
        width: '120px',
      },
      {
        Header: 'total_net_weight',
        accessor: 'totalNetWeight',
        width: '150px',
      },
      {
        Header: 'total_items',
        accessor: 'totalItems',
        width: '120px',
      },
      {
        Header: 'observations',
        accessor: 'observations',
        width: '200px',
      },
    ],
    []
  );

  // Define which columns are essential for mobile view
  const essentialColumns = [
    'despatch_id',
    'barcode',
    'despatch_status',
    'version',
    'mail_category',
    'gross_kg',
    'net_kg',
  ];

  // Filter columns based on screen size
  const filteredColumns = useMemo(() => {
    // On mobile, only show essential columns
    if (isMobile) {
      return columns.filter((column) => essentialColumns.includes(column.accessor as string));
    }
    // On desktop, show all columns
    return columns;
  }, [columns, isMobile, essentialColumns]);

  // Handle creating a new row
  const handleAddRow = () => {
    // Create a default new row with empty values or defaults
    const defaultNewRow = {
      id: 'new-row-' + Date.now(), // Temporary ID for UI purposes
      sampleId: '', // This will be the ibrsSn value we'll send to the API
      ibrsSn: '', // Used for display in the UI
      barcode: '',
      sampleStatus: 'NEW',
      version: 0,
      changeBy: '', // This might be filled with current user
      comment: false,
      delFlag: 0,
      validFlag: 0,
      mailCategory: '',
      formatCorrected: '',
      formatAnnounced: '',
      samplingMethod: '',
      netOrGrossAccounting: '',
      samplingRate: '',
      samplingDate: null,
      subsampleRate: '',
      // Get sendingPpo and receivingPpo from fileDetails if available
      sendingPpo: fileDetails?.operatorByOutboundPpo || '',
      oeFromCode: '',
      receivingPpo: fileDetails?.operatorByInboundPpo || '',
      oeToCode: '',
      exchangeOfficeByInboundOe: '',
      exchangeOfficeByOutboundOe: '',
      receptacleNumber: '',
      despatchNumber: '',
      despatchDate: null,
      despatchMailSubclass: '',
      grossKg: '',
      tareKg: '',
      netKg: '',
      labelWeight: '',
      ibrsKg: '',
      pkg: '',
      gkg: '',
      ekg: '',
      ckg: '',
      pitems: null,
      gitems: null,
      eitems: null,
      citems: null,
      receptacleType: '',
      receptacleCategory: '',
      receptacleMailSubclass: '',
      rplusV: '',
      fbag: '',
      stratum: '',
      receivedWeightStratum: '',
      sampledWeightStratum: '',
      effectiveSamplingRateStratum: '',
      receivedReceptaclesStratum: '',
      isNew: true, // Flag to identify this is a new row
    };

    // Set new row data and open the edit modal
    setNewRow(defaultNewRow);
    setSelectedRow(defaultNewRow);
    setIsAddingRow(true);
    setIsEditModalOpen(true);
  };

  // Handle notification close
  const handleNotificationClose = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  // Define handlers for cell editing
  const handleCellEdit = (row: any, column: string, value: any) => {
    // If this is the new row we're editing, update our local state
    if (isAddingRow && row.id === newRow?.id) {
      // For numeric fields that might be represented as strings (e.g., grossKg, netKg, ekg),
      // we preserve the string format if it comes from the API that way
      setNewRow((prev: any) => ({
        ...prev,
        [column]: value,
        // If user is editing ibrsSn, also update sampleId to match
        ...(column === 'ibrsSn' ? { sampleId: value } : {}),
      }));
    }
    // For existing rows, we'll handle this when saving
  };

  const handleRowSave = async (row: any, originalRow: any) => {
    // Prevent saving rows with valid_flag = 0
    if (row.validFlag === 0 && !isAddingRow) {
      setNotification({
        open: true,
        message: t('e09.viewSamples.cannotEditInvalidRow', 'Only valid rows can be edited'),
        type: 'error',
      });
      return false;
    }

    // Handle new row save
    if (isAddingRow && row.id === newRow?.id) {
      try {
        // Prepare data for API submission - remove UI-only fields
        const { id, isNew, className, ...apiData } = row;

        // Map ibrsSn to sampleId for API payload
        const payload = {
          ...apiData,
          // If ibrsSn is provided, use it as sampleId; otherwise use existing sampleId or empty string
          sampleId:
            apiData.ibrsSn !== null && apiData.ibrsSn !== undefined
              ? String(apiData.ibrsSn)
              : apiData.sampleId !== null && apiData.sampleId !== undefined
                ? String(apiData.sampleId)
                : '',
          fileId: numericFileId,
        };

        // Call the mutation to add a new sample
        const response = await addSampleMutation.mutateAsync(payload);

        // Check if the response contains a different dataSetId than current fileId
        if (response?.dataSetId && response.dataSetId.toString() !== fileId) {
          // Navigate to the correct dataset page
          navigate(
            `/e09/despatches/${response.dataSetId}${isEditMode ? '?editMode=true' : ''}`
          );
          return true;
        }

        // Clear the adding state and show success message
        setIsAddingRow(false);
        setNewRow(null);
        setNotification({
          open: true,
          message: t('e09.viewSamples.addSuccess', 'New despatch added successfully'),
          type: 'success',
        });

        return true; // Return true if save was successful
      } catch (error) {
        console.error('Error saving new row', error);
        setNotification({
          open: true,
          message: t('e09.viewSamples.addError', 'Failed to add new despatch'),
          type: 'error',
        });
        return false;
      }
    }

    // Handle updating an existing row
    try {
      // Prepare the data for the API by removing UI-specific fields
      // and mapping to the structure expected by the API
      const {
        id,
        className,
        isNew,
        // Remove any other UI-only fields here
        ...cleanData
      } = row;

      // Map ibrsSn to sampleId for API payload
      const payload = {
        ...cleanData,
        // If ibrsSn is provided, use it as sampleId; otherwise use existing sampleId or empty string
        sampleId:
          cleanData.ibrsSn !== null && cleanData.ibrsSn !== undefined
            ? String(cleanData.ibrsSn)
            : cleanData.sampleId !== null && cleanData.sampleId !== undefined
              ? String(cleanData.sampleId)
              : '',
        // Always set these IDs explicitly
        e09FileId: numericFileId,
        fileId: numericFileId,
      };

      // Call the mutation to update the sample
      const response = await updateSampleMutation.mutateAsync(payload);

      // Check if the response contains a different dataSetId than current fileId
      if (response?.dataSetId && response.dataSetId.toString() !== fileId) {
        // Navigate to the correct dataset page
        navigate(
          `/e09/despatches/${response.dataSetId}${isEditMode ? '?editMode=true' : ''}`
        );
        return true;
      }
      
      if (response?.dataSetId && response.dataSetId.toString() === fileId) {
        // Navigate to the correct dataset page
        window.location.reload();
        return true;
      }

      setNotification({
        open: true,
        message: t('e09.viewSamples.updateSuccess', 'Despatch updated successfully'),
        type: 'success',
      });

      setEditingRowId(null); // Clear editing state after save
      return true; // Return true if save was successful
    } catch (error) {
      console.error('Error updating row', error);
      setNotification({
        open: true,
        message: t('e09.viewSamples.updateError', 'Failed to update despatch'),
        type: 'error',
      });
      return false;
    }
  };

  const handleRowCancel = (row: any) => {
    // If we're canceling a new row, clear the adding state
    if (isAddingRow && row.id === newRow?.id) {
      setIsAddingRow(false);
      setNewRow(null);
    }

    setEditingRowId(null); // Clear editing state
  };

  // Context menu action handler
  const handleContextMenuAction = (action: ContextMenuAction, row: any) => {
    // Prevent editing rows with valid_flag = 0
    if (action === 'editSample') {
      // Check if the row has valid_flag = 0
      if (row.validFlag === 0) {
        setNotification({
          open: true,
          message: t('e09.viewSamples.cannotEditInvalidRow', 'Only valid rows can be edited'),
          type: 'error',
        });
        return;
      }

      // Set this row for editing and open the edit modal
      if (isEditMode && hasEditPermission) {
        setEditingRowId(row.sampleDbId || row.id);
        // If sample status is Final, show comment as empty in edit mode
        const rowForEdit = row.sampleStatus === 'F' || row.despatchStatus === 'F'
          ? { ...row, comment: false }
          : row;
        setSelectedRow(rowForEdit);
        setIsEditModalOpen(true);
      }
    } else if (action === 'editStratumData') {
      // Navigate to stratum data page
      if (fileId) {
        // For edit mode, navigate to edit mode of stratum page
        if (isEditMode) {
          navigate(`/e09/messages/stratum/${fileId}?editMode=true`);
        }
        // For view mode, navigate to view mode of stratum page
        else {
          navigate(`/e09/messages/stratum/${fileId}`);
        }
      }
    } else if (action === 'deleteAmendment' && row?.despatchStatus === 'D') {
      // Determine the identifier to send as sampleId; prefer existing sampleId/ibrsSn
      console.log('Row data for deletion:', row);
      const rawSampleId = row.ibrsSn;
      if (rawSampleId === undefined || rawSampleId === null || String(rawSampleId).trim() === '') {
        setNotification({
          open: true,
          message: t('e09.viewSamples.deleteInvalidSample', 'Invalid sampleId for deletion'),
          type: 'error',
        });
        return;
      }
      deleteSampleMutation.mutate(
        { sampleId: String(rawSampleId), fileId: numericFileId },
        {
          onSuccess: () => {
            setNotification({
              open: true,
              message: t('e09.viewSamples.deleteSuccess', 'Despatch deleted successfully'),
              type: 'success',
            });
          },
          onError: (err: any) => {
            const message = err?.response?.data?.message || err?.message || t('e09.viewSamples.deleteError', 'Failed to delete despatch');
            setNotification({ open: true, message, type: 'error' });
          },
        }
      );
    } else if (action === 'additionalSampleInfo') {
      console.log('Additional despatch information:', row);
      // Implement additional despatch information action logic here
      // This could open a modal with more detailed information
      setSelectedAIIRow(row);
      setIsAIIModalOpen(true);
    }
  };

  // Handle closing the edit modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedRow(null);
    setEditingRowId(null);

    // If we were adding a new row but canceled, clear that state too
    if (isAddingRow) {
      setIsAddingRow(false);
      setNewRow(null);
    }
  };

  // Handle closing the AII modal
  const handleCloseAIIModal = () => {
    setIsAIIModalOpen(false);
    setSelectedAIIRow(null);
  };

  // Define custom context menu actions for the samples table
  const customContextMenuActions = useMemo(() => {
    // For edit mode, return actions based on row status
    if (isEditMode) {
      return [
        {
          action: 'editSample' as ContextMenuAction,
          label: t('e09.viewSamples.contextMenu.editSample', 'Edit Despatch'),
          icon: <EditIcon />,
          isVisible: (row: any) => true, // Only visible if valid_flag is not 0
        },
        {
          action: 'deleteAmendment' as ContextMenuAction,
          label: t('e09.viewSamples.contextMenu.deleteAmendment', 'Delete Amendment'),
          icon: <DeleteIcon />,
          isVisible: (row: any) => row?.despatchStatus === 'D',
        },
        {
          action: 'additionalSampleInfo' as ContextMenuAction,
          label: t('e09.viewSamples.contextMenu.asi', 'AII (Additional IBRS Information)'),
          icon: <InfoOutlinedIcon />,
          isVisible: (row: any) => true, // Always visible
        },
      ];
    }
    // Options for view mode
    else if (isViewMode) {
      return [
        {
          action: 'additionalSampleInfo' as ContextMenuAction,
          label: t('e09.viewSamples.contextMenu.asi', 'AII (Additional IBRS Information)'),
          icon: <InfoOutlinedIcon />,
          isVisible: (row: any) => true, // Always visible
        },
      ];
    }
    // Return empty array if neither edit nor view mode
    else {
      return [];
    }
  }, [isEditMode, isViewMode, t]);

  // Update the handlers for validation buttons to make API requests
  const handleValidateAndCorrect = async () => {
    if (fileId) {
      setValidateCorrectLoading(true);
      // Clear any previous validation errors
      resetValidationErrors();

      try {
        // Use the React Query mutation hook instead of direct axios call
        const response = await validateFile.mutateAsync(numericFileId);

        // Handle successful response
        setNotification({
          open: true,
          message: t(
            'e09.viewSamples.validateCorrectSuccess',
            'Validation and correction initiated successfully. File is validated.'
          ),
          type: 'success',
        });

        // Refresh the page after showing the validation notification
        // Add a small delay to ensure the notification is seen
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } catch (error: any) {
        // Extract validation errors if they exist
        let errors: ValidationError[] = [];

        if (error.response?.data) {
          errors = extractValidationErrors(error.response.data);
        }

        if (errors.length > 0) {
          // Set validation errors to display in ErrorTable
          setValidationErrors(errors);

          // Show a general notification about validation errors
          setNotification({
            open: true,
            message: t(
              'e09.viewSamples.validationErrors',
              'Validation failed. Please review the errors below.'
            ),
            type: 'error',
          });
        } else {
          // Handle error with general message if no specific validation errors
          const errorMessage =
            error instanceof Error
              ? error.message
              : t(
                  'e09.viewSamples.validateCorrectError',
                  'Failed to initiate validation and correction'
                );

          console.error('Error validating and correcting:', error);
          setNotification({
            open: true,
            message: errorMessage,
            type: 'error',
          });
        }
      } finally {
        setValidateCorrectLoading(false);
      }
    }
  };

  const handleValidateAndFinalize = async () => {
    if (fileId) {
      setValidateFinalizeLoading(true);
      // Clear any previous validation errors
      resetValidationErrors();

      try {
        // Use the React Query mutation hook instead of direct axios call
        const response = await finalizeFile.mutateAsync(numericFileId);

        // Handle successful response
        setNotification({
          open: true,
          message: t(
            'e09.viewSamples.validateFinalizeSuccess',
            'Validation and finalization initiated successfully. File is accepted.'
          ),
          type: 'success',
        });

        // Navigate to /e09/despatches after showing the acceptance notification
        // Add a small delay to ensure the notification is seen
        setTimeout(() => {
          navigate('/e09/despatches');
        }, 1500);
      } catch (error: any) {
        // Extract validation errors if they exist
        let errors: ValidationError[] = [];

        if (error.response?.data) {
          errors = extractValidationErrors(error.response.data);
        }

        if (errors.length > 0) {
          // Set validation errors to display in ErrorTable
          setValidationErrors(errors);

          // Show a general notification about validation errors
          setNotification({
            open: true,
            message: t(
              'e09.viewSamples.finalizationErrors',
              'Finalization failed. Please review the errors below.'
            ),
            type: 'error',
          });
        } else {
          // Handle error with general message if no specific validation errors
          const errorMessage =
            error instanceof Error
              ? error.message
              : t(
                  'e09.viewSamples.validateFinalizeError',
                  'Failed to initiate validation and finalization'
                );

          console.error('Error validating and finalizing:', error);
          setNotification({
            open: true,
            message: errorMessage,
            type: 'error',
          });
        }
      } finally {
        setValidateFinalizeLoading(false);
      }
    }
  };

  // Helper function to check if a row is editable
  const isEditAllowed = (row: any): boolean => {
    return row?.validFlag !== 0;
  };

  const getTableContent = () => {
    if (!isValidFileId) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant='body1' color='error'>
            {t('e09.viewSamples.invalidFileId', 'Invalid file ID provided')}
          </Typography>
        </Box>
      );
    }

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (isError) {
      return (
        <Box sx={{ p: 2, color: 'error.main' }}>
          <Typography variant='body1'>
            {t('e09.viewSamples.error', 'Error loading despatch data')}
          </Typography>
          {error instanceof Error && (
            <Typography variant='body2' sx={{ mt: 1 }}>
              {error.message}
            </Typography>
          )}
        </Box>
      );
    }

    // Process data to add ID for proper row editing and ensure consistent data format
    let dataWithIds = dispatchData.map((item: any) => {
      // Process the item to ensure consistent data types
      const processedItem = {
        ...item,
        id: item.sampleDbId || item.id, // Ensure each row has an id property for editing
        // Map sampleId to ibrsSn for display in the UI
        ibrsSn: item.ibrsSn || item.sampleId,
        // Ensure sampleId is always a string
        sampleId:
          item.sampleId !== null && item.sampleId !== undefined ? String(item.sampleId) : '',
        // Add a className for disabled rows (valid_flag = 0)
        className: item.validFlag === 0 ? 'non-editable-row' : '',
      };

      // Preserve string representation for numeric fields if they come as strings
      // This ensures data consistency between what's displayed and what's sent to the API
      return processedItem;
    });

    // If we're adding a new row, add it to the data
    if (isAddingRow && newRow) {
      dataWithIds = [newRow, ...dataWithIds];
    }

    return (
      <Box>
        {isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Tooltip title='Swipe left and right to view all data columns'>
              <InfoIcon color='primary' sx={{ mr: 1, fontSize: 18 }} />
            </Tooltip>
            <Typography variant='caption' color='text.secondary'>
              {t(
                'e09.viewSamples.mobileNote',
                'Swipe horizontally to view all columns. Only showing essential columns on mobile.'
              )}
            </Typography>
          </Box>
        )}
        {isEditMode && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                variant='contained'
                color='secondary'
                startIcon={<VerifiedIcon />}
                onClick={handleValidateAndCorrect}
                disabled={validateCorrectLoading || !isValidateCorrectEnabled}
                fullWidth={isMobile}
                data-testid='e09-validate-correct-button'
                title={
                  !isValidateCorrectEnabled
                    ? t(
                        'e09.viewSamples.validateCorrectDisabled',
                        'Only drafts can be validated and corrected'
                      )
                    : ''
                }
              >
                {validateCorrectLoading ? (
                  <>
                    <CircularProgress size={20} color='inherit' sx={{ mr: 1 }} />
                    {t('e09.viewSamples.validating', 'Validating...')}
                  </>
                ) : (
                  t('e09.viewSamples.validateCorrect', 'Validate & Correct')
                )}
              </Button>
              <Button
                variant='contained'
                color='success'
                startIcon={!validateFinalizeLoading && <DoneAllIcon />}
                onClick={handleValidateAndFinalize}
                disabled={validateFinalizeLoading || !isValidateFinalizeEnabled}
                fullWidth={isMobile}
                data-testid='e09-validate-finalize-button'
                title={
                  !isValidateFinalizeEnabled
                    ? t('e09.viewSamples.validateFinalizeDisabled', 'Already finalized')
                    : ''
                }
              >
                {validateFinalizeLoading ? (
                  <>
                    <CircularProgress size={20} color='inherit' sx={{ mr: 1 }} />
                    {t('e09.viewSamples.finalizing', 'Finalizing...')}
                  </>
                ) : (
                  t('e09.viewSamples.validateFinalize', 'Validate & Finalize')
                )}
              </Button>
              {isEditMode && (
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<AddIcon />}
                  onClick={handleAddRow}
                  disabled={isAddingRow || addSampleMutation.isPending}
                  fullWidth={isMobile}
                  data-testid='e09-add-despatch-button'
                >
                  {t('e09.viewSamples.addSample', 'Add New Despatch')}
                </Button>
              )}
            </Stack>
          </Box>
        )}

        {/* Display validation errors if they exist */}
        {validationErrors.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <PaginatedErrorTable errors={validationErrors} fileName={`e09 File ID: ${fileId}`} />
          </Box>
        )}
        <Table
          data={dataWithIds}
          columns={filteredColumns}
          moduleName={t('e09.viewSamples.tableTitle', `e09 DESPATCHES`)}
          totalCount={
            isAddingRow
              ? (dispatchResponse?.totalElements || 0) + 1
              : dispatchResponse?.totalElements
          }
          pageSize={pageSize}
          page={page}
          onPageSizeChange={handlePageSizeChange}
          onPageChange={handlePageChange}
          onSort={handleSort}
          defaultSort={sorts.length > 0 ? sorts[0] : (sortField && sortDirection ? { column: sortField, direction: sortDirection } : undefined)}
          defaultSorts={sorts.length > 0 ? sorts : undefined}
          defaultFilters={filters.length > 0 ? filters : undefined}
          initialSearchColumn={searchColumn}
          initialSearchTerm={searchTerm}
          disableAutoSizing={false}
          viewMode={!isEditMode}
          editMode={isEditMode}
          onCellEdit={isEditMode ? handleCellEdit : undefined}
          onRowSave={isEditMode ? handleRowSave : undefined}
          onRowCancel={isEditMode ? handleRowCancel : undefined}
          readOnlyColumns={['sampleDbId', 'fileId', 'version']}
          data-testid='e09-despatches-table'
          excludedColumns={[
            'despatchStatus',
            'aii',
            'asi',
            'sampleStatus',
            'stratum',
            'receivedWeightStratum',
            'sendingPpo',
            'receivingPpo',
            'sampledWeightStratum',
            'effectiveSamplingRateStratum',
            'received_weight_stratum',
            'sampled_weight_stratum',
            'effective_sampling_rate_stratum',
            'receivedReceptaclesStratum',
            'received_receptacles_stratum',
            'sampleId', // Exclude sampleId from display since we're showing ibrsSn instead
          ]}
          requiredFields={[
            'version',
            'delFlag',
            'validFlag',
            'format',
            'despatchId',
            'exemptFlag',
            'normalNetWeight',
            'normalItems',
            'bulkyNetWeight',
            'bulkyItems',
            'totalNetWeight',
            'totalItems',
            'mailCategory',
            'oeFromCode',
            'oeToCode',
            'despatchNumber',
            'despatchDate',
            'despatchMailSubclass',
          ]}
          textAreaColumns={['comment']}
          dateColumns={['samplingDate', 'despatchDate', 'sampling_date', 'despatch_date']}
          defaultReadOnlyColumns={isAddingRow ? ['validFlag', 'delFlag', 'comment'] : ['validFlag']}
          priorityReadOnlyColumns={['ibrsSn', 'version', 'changeBy']}
          rowEventHandlers={{
            onContextMenuAction: handleContextMenuAction,
          }}
          customActions={customContextMenuActions}
          hideDefaultActions={false} // Show default actions
          isRowEditable={isEditAllowed} // Pass the isEditAllowed function to determine if a row is editable
        />
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='e09-view-samples-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title={`e09 Despatches - File ID: ${fileId} - ${fileDetails?.fileName}${isEditMode ? ' (Edit Mode)' : ''}`}
            subtitle='e09 Message'
            onToggleSidebar={handleSidebarToggle}
            onSubtitleClick={handleSubtitleClick}
            isSidebarOpen={sidebarOpen}
            showFilters={{
              customStatus: true
            }}
            customFilters={{
              status: statusFilter,
              onStatusChange: handleStatusFilterChange
            }}
            data-testid='e09-section-bar'
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }} height='100%' data-testid='e09-sidebar-container'>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='e09-samples-main-content'>
            {permissionLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid='e09-loading-spinner'>
                <CircularProgress />
              </Box>
            ) : !hasPermission ? (
              <Alert severity='warning' data-testid='e09-permission-warning'>
                You don't have permission to view this section. Required permission: 'View
                Despatches' or other e09 permissions.
              </Alert>
            ) : isEditMode && !hasEditPermission ? (
              <Alert severity='warning' data-testid='e09-edit-permission-warning'>
                You don't have permission to edit despatches. Required permission: 'Edit Despatches'
              </Alert>
            ) : (
              <Box component={Paper} sx={{ mb: 2 }} data-testid='e09-content-container'>
                <ContentPaper>{getTableContent()}</ContentPaper>
              </Box>
            )}
          </MainContent>
        </Grid>

        {/* Notification Snackbar */}
        <NotificationSnackbar
          open={notification.open}
          message={notification.message}
          severity={notification.type}
          onClose={handleNotificationClose}
          data-testid="e09-view-samples-notification"
        />

        {/* EditModal for sample editing */}
        {isEditMode && (
          <E09EditModal
            isOpen={isEditModalOpen}
            onClose={handleCloseEditModal}
            onSave={handleRowSave}
            row={selectedRow}
            columns={columns}
            data-testid='e09-edit-modal'
            excludedColumns={[
              'despatchStatus',
              'aii',
              'asi',
              'sampleStatus',
              'stratum',
              'receivedWeightStratum',
              'sendingPpo',
              'receivingPpo',
              'sampledWeightStratum',
              'effectiveSamplingRateStratum',
              'received_weight_stratum',
              'sampled_weight_stratum',
              'effective_sampling_rate_stratum',
              'receivedReceptaclesStratum',
              'received_receptacles_stratum',
              'sampleId', // Exclude sampleId from display since we're showing ibrsSn instead
            ]}
            requiredFields={[
              'mailCategory',
              'despatchMailSubclass',
              'format',
              'oeFromCode',
              'oeToCode',
              'exemptFlag',
              'despatchDate',
              'despatchNumber',
              'despatchId',
              'normalNetWeight',
              'normalItems',
              'bulkyNetWeight',
              'bulkyItems',
              'totalNetWeight',
              'totalItems',
            ]}
            textAreaColumns={['comments']}
            dateColumns={['despatchDate']}
            defaultReadOnlyColumns={isAddingRow ? ['validFlag', 'delFlag'] : ['validFlag']}
            priorityReadOnlyColumns={['ibrsSn', 'version', 'changeBy']}
            readOnlyColumns={['sampleDbId', 'fileId', 'version']}
            title={
              isAddingRow
                ? t('e09.viewSamples.addSample', 'Add New Despatch')
                : t('e09.viewSamples.editSample', 'Edit Despatch')
            }
          />
        )}

        {/* AII Modal for additional IBRS information */}
        <AIIModal
          isOpen={isAIIModalOpen}
          onClose={handleCloseAIIModal}
          row={selectedAIIRow}
          dataSetId={numericFileId}
          fileDetails={fileDetails}
        />
      </Grid>
    </Box>
  );
};

export default ViewSamples;
